import React, { forwardRef, useLayoutEffect } from 'react';
import { Scrollable } from './Scrollable';
import {
	useObservableRef,
	useLazyConst,
	useObservableRefValue,
} from '@chkp/mwc-common';

// react-window doesn't allow passing props to the outer/inner elements: https://github.com/bvaughn/react-window/issues/242
// Solution - creating the type internally & passing props via observable ref
const useScrollbarsOuterElementType = ({
	ref,
	onScrollUpdate,
	dragTooltip,
	onDragStart,
	onDrag,
	onDragEnd,
	hasScrollXObservable,
	hasScrollYObservable,
	debug,
}) => {
	const propsObservable = useObservableRef({
		onScrollUpdate,
		dragTooltip,
		debug,
	});

	useLayoutEffect(() => {
		propsObservable.current = {
			ref,
			onUpdate: onScrollUpdate,
			dragTooltip,
			onDragStart,
			hasScrollXObservable,
			hasScrollYObservable,
			onDrag,
			onDragEnd,
			debug,
		};
	}, [
		debug,
		dragTooltip,
		hasScrollXObservable,
		hasScrollYObservable,
		onDrag,
		onDragEnd,
		onDragStart,
		onScrollUpdate,
		propsObservable,
		ref,
	]);

	// Keep outerElementType intact, otherwise scrollbars are unstable.
	// props for the Scrollable component are passed via observable ref.
	const Type = useLazyConst(() =>
		forwardRef((outerElementProps, outerElementRef) => {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			const scrollbarProps = useObservableRefValue(propsObservable);
			return (
				<Scrollable
					{...outerElementProps}
					{...scrollbarProps}
					forwardedRef={outerElementRef}
				/>
			);
		}),
	);

	return Type;
};

export { useScrollbarsOuterElementType };
